import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import {
  AlertTriangle, ArrowDown, ArrowUp, ArrowUpRight, CalendarDays, ChevronLeft, ChevronRight,
  Home as HomeIcon, Package, Camera, Plus, Search, Table2, X,
} from 'lucide-react';
import { Project, ProjectFilter } from '../../types';
import {
  createProject, getDeletedProjects, getProjectsByFilterWithCounts, permanentlyDeleteProject, restoreProject, updateProject,
} from '../../services/projectService';
import { useNotifications } from '../../contexts/NotificationContext';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { Button } from '../ui/Button';
import { Input, controlClass } from '../ui/Field';
import { DateField, formatSvDateShort } from '../ui/DateField';
import { Popover } from '../ui/Popover';
import { AssigneeAvatars, getAssignees } from '../ui/AssigneeAvatars';
import { EmptyState } from '../ui/EmptyState';
import { Skeleton } from '../ui/Skeleton';
import { getStatusLabel } from '../ui/StatusBadge';
import ConfirmDialog from '../ConfirmDialog';
import { StatusSelect } from './StatusSelect';
import { startOfDay, sameDay, dateOf, nextDate, needsAttention, isActive, computeLanes, KIND_DOT, KIND_TEXT } from '../../utils/lanes';

type Counts = Record<string, { roomCount: number; itemCount: number; photoCount: number; checklistCount: number; damageReportCount: number }>;
type SortKey = 'next' | 'address' | 'status' | 'evaluation' | 'pickup';
type View = 'table' | 'calendar';

const FILTER_KEY = 'stadsauktion_filter';
const VIEW_KEY = 'stadsauktion_desktop_view';

const STATUS_RANK: Record<Project['status'], number> = { värderad: 1, planerad: 2, bostad: 3, hem: 3, väntar: 4, avslutad: 5, avbruten: 6 };

/** Module-level so it keeps its identity between renders (see MobileLanes). */
const SortHeader: React.FC<{ k: SortKey; sort: { key: SortKey; dir: 'asc' | 'desc' }; onToggle: (k: SortKey) => void; children: React.ReactNode; className?: string }> = ({ k, sort, onToggle, children, className = '' }) => {
  // The default order follows the next date, so both date columns carry its cue.
  const active = sort.key === k || (sort.key === 'next' && (k === 'evaluation' || k === 'pickup'));
  return (
    <th scope="col" aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} className={`px-3 py-2.5 text-left ${className}`}>
      <button type="button" onClick={() => onToggle(k)} className={`inline-flex items-center gap-1 h-[32px] -my-1 -ml-1.5 px-1.5 rounded text-[12px] font-semibold uppercase tracking-[0.04em] hover:bg-[var(--bg-surface-alt)] ${active ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
        {children}
        {active && (sort.key === k || k === 'evaluation') && (sort.dir === 'asc' ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />)}
      </button>
    </th>
  );
};

const FILTER_LABEL = (f: ProjectFilter) => f === 'active' ? 'Aktiva' : f === 'all' ? 'Alla' : f === 'needs_attention' ? 'Behöver åtgärd' : getStatusLabel(f);

/**
 * Desktop home: a working table with inline status and dates, a lane of what
 * needs attention, and a single visible way to create a project.
 */
export function DesktopHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  const { addNotification } = useNotifications();

  const [projects, setProjects] = useState<Project[]>([]);
  const [counts, setCounts] = useState<Counts>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'next', dir: 'asc' });
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) as View) || 'table');
  const [calendarCursor, setCalendarCursor] = useState(new Date());
  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const quickRef = useRef<HTMLButtonElement>(null);
  const [trash, setTrash] = useState<Array<Project & { deletedAt: any; expiresAt: any }>>([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [permanentId, setPermanentId] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const filter = (params.get('filter') as ProjectFilter | null) || (localStorage.getItem(FILTER_KEY) as ProjectFilter | null) || 'active';
  const showTrash = params.get('trash') === '1';

  useEffect(() => { localStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { projects: all, counts: c } = await getProjectsByFilterWithCounts('all');
      setProjects(all); setCounts(c); setError(null);
    } catch (e) {
      console.error('Home fetch failed:', e);
      setError('Det gick inte att hämta projekten. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!showTrash) return;
    setTrashLoading(true);
    getDeletedProjects().then(setTrash).catch(console.error).finally(() => setTrashLoading(false));
  }, [showTrash]);

  // --- lanes ---------------------------------------------------------------
  const lanes = useMemo(() => computeLanes(projects), [projects]);

  // --- table rows ----------------------------------------------------------
  const rows = useMemo(() => {
    let list = projects;
    if (filter === 'active') list = list.filter(isActive);
    else if (filter === 'needs_attention') list = list.filter(p => !!needsAttention(p));
    else if (filter !== 'all') list = list.filter(p => p.status === filter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(p => [p.address.street, p.address.city, p.contact.name, p.evaluator, ...(p.pickupTeam ?? [])].some(s => (s || '').toLowerCase().includes(q)));

    const today = startOfDay(new Date());
    const dir = sort.dir === 'asc' ? 1 : -1;
    const cmp = (a: Project, b: Project): number => {
      switch (sort.key) {
        case 'address': return a.address.street.localeCompare(b.address.street, 'sv') * dir;
        case 'status': return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * dir;
        case 'evaluation': return ((dateOf(a.dates.evaluation)?.getTime() ?? Infinity) - (dateOf(b.dates.evaluation)?.getTime() ?? Infinity)) * dir;
        case 'pickup': return ((dateOf(a.dates.scheduledPickup)?.getTime() ?? Infinity) - (dateOf(b.dates.scheduledPickup)?.getTime() ?? Infinity)) * dir;
        default: {
          // Upcoming first (soonest on top), then passed (most recent first), then undated.
          const na = nextDate(a).date; const nb = nextDate(b).date;
          const ga = !na ? 2 : na >= today ? 0 : 1; const gb = !nb ? 2 : nb >= today ? 0 : 1;
          if (ga !== gb) return (ga - gb) * dir;
          if (!na || !nb) return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * dir;
          return (ga === 0 ? na.getTime() - nb.getTime() : nb.getTime() - na.getTime()) * dir;
        }
      }
    };
    return [...list].sort(cmp);
  }, [projects, filter, search, sort]);

  const firstPassedIndex = useMemo(() => {
    if (sort.key !== 'next' || sort.dir !== 'asc') return -1;
    const today = startOfDay(new Date());
    return rows.findIndex(p => { const d = nextDate(p).date; return !!d && d < today; });
  }, [rows, sort]);

  // --- inline edits --------------------------------------------------------
  const patchProject = async (p: Project, patch: Partial<Project>, label: string) => {
    if (!isOnline) { addNotification({ type: 'error', message: 'Du är offline. Ändringen sparades inte.', duration: 4000 }); return; }
    const previous = projects;
    setProjects(list => list.map(x => (x.id === p.id ? { ...x, ...patch } as Project : x)));
    setSavingRow(p.id);
    try {
      await updateProject(p.id, patch);
    } catch (e) {
      console.error('Inline update failed:', e);
      setProjects(previous);
      addNotification({ type: 'error', message: `Kunde inte spara ${label}. Försök igen.`, duration: 4000 });
    } finally {
      setSavingRow(null);
    }
  };

  const setStatus = (p: Project, status: Project['status']) => patchProject(p, { status }, 'status');
  const setEvaluation = (p: Project, d: Date | null) => patchProject(p, { dates: { ...p.dates, evaluation: d ? Timestamp.fromDate(d) : null } }, 'hembesök');
  const setPickup = (p: Project, d: Date | null) => {
    const patch: Partial<Project> = { dates: { ...p.dates, scheduledPickup: d ? Timestamp.fromDate(d) : null } };
    if (d && (p.status === 'bostad' || p.status === 'hem' || p.status === 'värderad')) patch.status = 'planerad';
    patchProject(p, patch, 'hämtning');
  };

  const toggleSort = (key: SortKey) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // --- trash ---------------------------------------------------------------
  const restore = async (id: string) => {
    try {
      setRestoring(id);
      await restoreProject(id);
      setTrash(t => t.filter(p => p.id !== id));
      await fetchAll();
      addNotification({ type: 'success', message: 'Projektet har återställts.', duration: 3000 });
    } catch (e) {
      console.error(e);
      addNotification({ type: 'error', message: 'Kunde inte återställa projektet.', duration: 4000 });
    } finally { setRestoring(null); }
  };
  const confirmPermanent = async () => {
    if (!permanentId) return;
    try {
      await permanentlyDeleteProject(permanentId);
      setTrash(t => t.filter(p => p.id !== permanentId));
      addNotification({ type: 'success', message: 'Projektet raderades permanent.', duration: 3000 });
    } catch (e) {
      console.error(e);
      addNotification({ type: 'error', message: 'Kunde inte radera projektet.', duration: 4000 });
    } finally { setPermanentId(null); }
  };

  // --- calendar ------------------------------------------------------------
  const calendarDays = useMemo(() => {
    const y = calendarCursor.getFullYear(); const m = calendarCursor.getMonth();
    const first = new Date(y, m, 1); const last = new Date(y, m + 1, 0);
    const start = new Date(first); start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    const end = new Date(last); end.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7)));
    const days: Date[] = []; const c = new Date(start);
    while (c <= end) { days.push(new Date(c)); c.setDate(c.getDate() + 1); }
    return days;
  }, [calendarCursor]);

  const activeCount = projects.filter(isActive).length;

  return (
    <div className="px-8 py-6 max-w-[1200px] mx-auto">
      <ConfirmDialog
        isOpen={permanentId !== null}
        title="Radera permanent"
        message="Projektet raderas för gott och kan inte återställas."
        confirmLabel="Radera permanent"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmPermanent}
        onCancel={() => setPermanentId(null)}
      />

      {/* Header */}
      <header className="flex items-center justify-between gap-6 mb-6">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="text-[28px] leading-[34px] font-semibold tracking-tight text-[var(--text-primary)]">Projekt</h1>
          {!loading && <span className="text-sm text-[var(--text-secondary)] tabular-nums whitespace-nowrap">{activeCount} aktiva</span>}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-52 xl:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none" aria-hidden="true" />
            <Input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Sök adress, kontakt, person" aria-label="Sök projekt" className="pl-9" />
          </div>
          <div role="group" aria-label="Vy" className="inline-flex h-[38px] items-center rounded-ios-control border border-[var(--border-default)] bg-[var(--bg-surface)] p-[2px]">
            {([['table', Table2, 'Tabell'], ['calendar', CalendarDays, 'Kalender']] as const).map(([v, Icon, label]) => (
              <button key={v} type="button" aria-pressed={view === v} aria-label={label} title={label} onClick={() => setView(v)}
                className={`h-[32px] px-2.5 rounded-ios-control-inner inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors ${view === v ? 'bg-[var(--bg-surface-alt)] text-[var(--text-primary)] shadow-ios-control' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
                <Icon size={15} aria-hidden="true" /><span className="hidden xl:inline">{label}</span>
              </button>
            ))}
          </div>
          <Button ref={quickRef} variant="primary" icon={<Plus size={16} />} aria-haspopup="dialog" aria-expanded={quickOpen} onClick={() => setQuickOpen(o => !o)}>
            Nytt projekt
          </Button>
          <Popover open={quickOpen} onClose={() => setQuickOpen(false)} anchorRef={quickRef} align="end" width={360} aria-label="Nytt projekt">
            <QuickAdd onClose={() => setQuickOpen(false)} onCreated={(id) => { setQuickOpen(false); fetchAll(); navigate(`/projects/${id}`); }} />
          </Popover>
        </div>
      </header>

      {error && (
        <div role="alert" className="mb-5 rounded-ios-card border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Needs-action lane */}
      {!loading && !showTrash && (
        <div className="grid grid-cols-3 gap-4 mb-6 items-start [&>section]:min-h-[180px]">
          <Lane title="Idag" count={lanes.today.length} empty="Inget bokat idag" tone="accent">
            {lanes.today.map(p => <LaneRow key={p.id} p={p} kind={nextDate(p).kind} meta={nextDate(p).time ? ` kl ${nextDate(p).time}` : ''} />)}
          </Lane>
          <Lane title="Denna vecka" count={lanes.week.length} empty="Inget mer den här veckan">
            {lanes.week.map(p => <LaneRow key={p.id} p={p} kind={nextDate(p).kind} meta={` ${formatSvDateShort(nextDate(p).date)}${nextDate(p).time ? ` kl ${nextDate(p).time}` : ''}`} />)}
          </Lane>
          <Lane title="Behöver åtgärd" count={lanes.attention.length} empty="Allt har ett nästa steg" tone={lanes.attention.length > 0 ? 'warn' : undefined}>
            {lanes.attention.map(({ p, why }) => <LaneRow key={p.id} p={p} meta={why} warn />)}
          </Lane>
        </div>
      )}

      {/* Toolbar */}
      {!showTrash && (
        <div className="flex items-center justify-between gap-4 mb-3">
          <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">
            {FILTER_LABEL(filter)} <span className="font-normal text-[var(--text-secondary)] tabular-nums">· {rows.length}</span>
          </h2>
          {filter !== 'active' && (
            <Link to="/?filter=active" className="inline-flex items-center gap-1 h-[32px] px-2.5 rounded-ios-control text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)]"><X size={14} /> Visa aktiva</Link>
          )}
        </div>
      )}

      {/* Content */}
      {showTrash ? (
        <TrashPanel items={trash} loading={trashLoading} restoring={restoring} onRestore={restore} onDelete={setPermanentId} />
      ) : loading ? (
        <div className="rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 space-y-3" aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-11 w-full" />)}
        </div>
      ) : view === 'calendar' ? (
        <div className="rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
            <Button size="sm" variant="ghost" aria-label="Föregående månad" onClick={() => setCalendarCursor(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}><ChevronLeft size={16} /></Button>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-[var(--text-primary)] capitalize">{calendarCursor.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}</span>
              <Button size="sm" variant="secondary" onClick={() => setCalendarCursor(new Date())}>Idag</Button>
            </div>
            <Button size="sm" variant="ghost" aria-label="Nästa månad" onClick={() => setCalendarCursor(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}><ChevronRight size={16} /></Button>
          </div>
          <div className="grid grid-cols-7 border-b border-[var(--border-subtle)] text-center text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">
            {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(d => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map(day => {
              const inMonth = day.getMonth() === calendarCursor.getMonth();
              const today = sameDay(day, new Date());
              const items = rows.flatMap(p => {
                const out: { p: Project; kind: 'pickup' | 'evaluation'; time?: string }[] = [];
                if (sameDay(dateOf(p.dates.scheduledPickup), day)) out.push({ p, kind: 'pickup', time: p.dates.scheduledPickupTime });
                if (sameDay(dateOf(p.dates.evaluation), day)) out.push({ p, kind: 'evaluation', time: p.dates.evaluationStartTime });
                return out;
              }).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
              return (
                <div key={day.toISOString()} className={`min-h-[112px] border-b border-r border-[var(--border-subtle)] p-1.5 ${inMonth ? '' : 'bg-[var(--bg-primary)]'}`}>
                  <div className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] tabular-nums ${today ? 'bg-accent-600 font-semibold text-white' : inMonth ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}`}>{day.getDate()}</div>
                  <div className="space-y-0.5">
                    {items.map(({ p, kind, time }) => (
                      <Link key={`${p.id}-${kind}`} to={`/projects/${p.id}`} className={`block truncate rounded px-1.5 py-0.5 text-[12px] font-medium ${kind === 'pickup' ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'}`} aria-label={`${kind === 'pickup' ? 'Hämtning' : 'Hembesök'}: ${p.address.street}`}>
                        {time ? `${time} ` : ''}{p.address.street}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)]">
          <EmptyState
            icon={HomeIcon}
            title={search ? 'Inga träffar' : projects.length === 0 ? 'Börja med ett projekt' : `Inga projekt under ${FILTER_LABEL(filter)}`}
            description={search ? `Inget projekt matchar "${search}".` : projects.length === 0 ? 'Registrera bostaden du ska värdera. Den går sedan vidare: klar för hämtning, planerad, avslutad.' : 'Byt filter i sidopanelen eller skapa ett nytt projekt.'}
            action={search ? <Button variant="secondary" onClick={() => setSearch('')}>Rensa sökning</Button> : <Button variant="primary" icon={<Plus size={16} />} onClick={() => setQuickOpen(true)}>Nytt projekt</Button>}
          />
        </div>
      ) : (
        <div className="rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] overflow-hidden">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[23%] xl:w-[24%]" /><col className="w-[22%] xl:w-[17%]" /><col className="w-[22%] xl:w-[16%]" /><col className="w-[22%] xl:w-[16%]" /><col className="w-[9%] xl:w-[8%]" /><col className="hidden xl:table-column" /><col className="w-10" />
            </colgroup>
            <thead>
              <tr className="border-b border-[var(--border-subtle)]">
                <SortHeader k="address" sort={sort} onToggle={toggleSort}>Adress</SortHeader>
                <SortHeader k="status" sort={sort} onToggle={toggleSort}>Status</SortHeader>
                <SortHeader k="evaluation" sort={sort} onToggle={toggleSort}>Hembesök</SortHeader>
                <SortHeader k="pickup" sort={sort} onToggle={toggleSort}>Hämtning</SortHeader>
                <th scope="col" className="px-3 py-2.5 text-left text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">Ansvarig</th>
                <th scope="col" className="px-3 py-2.5 text-left text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)] whitespace-nowrap hidden xl:table-cell">Rum · Objekt · Foton</th>
                <th scope="col" className="px-3 py-2.5"><span className="sr-only">Öppna</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {rows.map((p, i) => {
                const why = needsAttention(p);
                const c = counts[p.id];
                const passedDivider = i === firstPassedIndex && i > 0;
                const evalDate = dateOf(p.dates.evaluation); const pickupDate = dateOf(p.dates.scheduledPickup);
                return (
                  <React.Fragment key={p.id}>
                    {passedDivider && (
                      <tr aria-hidden="true"><td colSpan={7} className="bg-[var(--bg-primary)] px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">Passerade datum</td></tr>
                    )}
                    <tr
                      className={`group cursor-pointer transition-colors hover:bg-[var(--bg-surface-hover)] ${savingRow === p.id ? 'opacity-60' : ''} ${i >= firstPassedIndex && firstPassedIndex >= 0 ? 'text-[var(--text-secondary)]' : ''}`}
                      onClick={() => navigate(`/projects/${p.id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/projects/${p.id}`); }}
                      tabIndex={0}
                      aria-label={`Öppna ${p.address.street}`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="min-w-0">
                            <div className="text-[15px] font-medium text-[var(--text-primary)] truncate">{p.address.street}</div>
                            <div className="text-[13px] text-[var(--text-secondary)] truncate">
                              {p.address.city}{why && <span className="ml-2 inline-flex items-center gap-1 text-amber-800 dark:text-amber-300"><AlertTriangle size={12} aria-hidden="true" />{why}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                        <StatusSelect compact value={p.status} disabled={savingRow === p.id} onChange={s => setStatus(p, s)} />
                      </td>
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                        <DateField compact value={evalDate} detail={p.dates.evaluationStartTime || undefined} onChange={d => setEvaluation(p, d)} placeholder="Välj datum" aria-label={`Hembesök för ${p.address.street}`} />
                      </td>
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                        <DateField compact value={pickupDate} detail={p.dates.scheduledPickupTime || undefined} onChange={d => setPickup(p, d)} placeholder="Välj datum" aria-label={`Hämtning för ${p.address.street}`} />
                      </td>
                      <td className="px-3 py-2"><AssigneeAvatars names={getAssignees(p)} /></td>
                      <td className="px-3 py-2 hidden xl:table-cell">
                        {c && (
                          <div className="flex items-center gap-3 text-[13px] text-[var(--text-secondary)] tabular-nums">
                            <span className="inline-flex items-center gap-1" title="Rum"><HomeIcon size={13} aria-hidden="true" />{c.roomCount}<span className="sr-only"> rum</span></span>
                            <span className="inline-flex items-center gap-1" title="Objekt"><Package size={13} aria-hidden="true" />{c.itemCount}<span className="sr-only"> objekt</span></span>
                            <span className="inline-flex items-center gap-1" title="Foton"><Camera size={13} aria-hidden="true" />{c.photoCount}<span className="sr-only"> foton</span></span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <ArrowUpRight size={16} className="inline text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity" aria-hidden="true" />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

// --- small pieces ----------------------------------------------------------

const LANE_PREVIEW = 3;

/**
 * "+ N till" expands the lane in place (scrolling inside the card past ten
 * rows) rather than opening a modal or a filter view: the person asked to
 * see the rest of this list, not to go somewhere else.
 */
const Lane: React.FC<{ title: string; count: number; empty: string; tone?: 'accent' | 'warn'; children: React.ReactNode[] | React.ReactNode }> = ({ title, count, empty, tone, children }) => {
  const [expanded, setExpanded] = useState(false);
  const rows = React.Children.toArray(children);
  const shown = expanded ? rows : rows.slice(0, LANE_PREVIEW);
  return (
  <section aria-label={title} className={`flex flex-col rounded-ios-card border bg-[var(--bg-surface)] ${tone === 'warn' ? 'border-amber-300 dark:border-amber-700/60' : 'border-[var(--border-default)]'}`}>
    <header className="flex items-center justify-between px-4 pt-3 pb-2">
      <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</h2>
      <span className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1.5 text-[12px] font-semibold tabular-nums ${count === 0 ? 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]' : tone === 'warn' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-200'}`}>{count}</span>
    </header>
    <div className="flex-1 flex flex-col px-2 pb-2">
      {count === 0 ? (
        <p className="flex-1 flex items-center justify-center px-2 pb-3 text-[13px] text-[var(--text-secondary)]">{empty}</p>
      ) : (
        <div className={expanded ? 'max-h-[400px] overflow-y-auto' : ''}>{shown}</div>
      )}
      {count > LANE_PREVIEW && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(e => !e)}
          className="flex items-center px-2 h-[32px] -ml-0 text-[13px] font-medium text-accent-700 dark:text-accent-300 hover:bg-[var(--bg-surface-alt)] rounded-ios-control w-fit"
        >
          {expanded ? 'Visa färre' : `+ ${count - LANE_PREVIEW} till`}
        </button>
      )}
    </div>
  </section>
  );
};

const LaneRow: React.FC<{ p: Project; meta: string; warn?: boolean; kind?: 'pickup' | 'evaluation' }> = ({ p, meta, warn, kind }) => (
  <Link to={`/projects/${p.id}`} className="flex items-center gap-3 px-2 h-[40px] rounded-ios-control hover:bg-[var(--bg-surface-hover)]">
    {kind && <span aria-hidden="true" className={`h-2 w-2 rounded-full flex-shrink-0 ${KIND_DOT[kind]}`} />}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium text-[var(--text-primary)]">{p.address.street}</span>
      <span className={`block truncate text-[12px] ${warn ? 'text-amber-800 dark:text-amber-300' : 'text-[var(--text-secondary)]'}`}>
        {kind ? <><span className={`font-medium ${KIND_TEXT[kind]}`}>{kind === 'pickup' ? 'Hämtning' : 'Hembesök'}</span>{meta}</> : meta}
      </span>
    </span>
    <span className="hidden xl:inline text-[12px] text-[var(--text-secondary)]">{getStatusLabel(p.status)}</span>
  </Link>
);

const TrashPanel: React.FC<{ items: Array<Project & { deletedAt: any; expiresAt: any }>; loading: boolean; restoring: string | null; onRestore: (id: string) => void; onDelete: (id: string) => void }> = ({ items, loading, restoring, onRestore, onDelete }) => (
  <section aria-label="Papperskorg" className="rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)]">
    <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-subtle)]">
      <div>
        <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Papperskorg</h2>
        <p className="text-[13px] text-[var(--text-secondary)]">Borttagna projekt sparas i 7 dagar och kan återställas.</p>
      </div>
      <Link to="/" className="inline-flex items-center gap-1 h-[32px] px-2.5 rounded-ios-control text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)]"><ChevronLeft size={14} /> Till projekten</Link>
    </header>
    {loading ? (
      <div className="p-5 space-y-3">{[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
    ) : items.length === 0 ? (
      <p className="px-5 py-8 text-center text-sm text-[var(--text-secondary)]">Papperskorgen är tom.</p>
    ) : (
      <ul className="divide-y divide-[var(--border-subtle)]">
        {items.map(p => {
          const deleted = p.deletedAt?.toDate ? p.deletedAt.toDate() : null;
          const expires = p.expiresAt?.toDate ? p.expiresAt.toDate() : null;
          return (
            <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{p.address.street}, {p.address.city}</p>
                <p className="text-[13px] text-[var(--text-secondary)]">Borttaget {deleted ? formatSvDateShort(deleted) : '–'} · Raderas {expires ? formatSvDateShort(expires) : '–'}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="secondary" loading={restoring === p.id} onClick={() => onRestore(p.id)}>Återställ</Button>
                <Button size="sm" variant="destructive" onClick={() => onDelete(p.id)}>Radera permanent</Button>
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

/** Popover quick-add: address first, everything else optional. */
const QuickAdd: React.FC<{ onClose: () => void; onCreated: (id: string) => void }> = ({ onClose, onCreated }) => {
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);

  const save = async () => {
    if (!street.trim()) { setErr('Skriv in en adress.'); first.current?.focus(); return; }
    if (!isOnline) { setErr('Du är offline.'); return; }
    try {
      setSaving(true); setErr(null);
      const d = date ? new Date(date) : null; if (d) d.setHours(0, 0, 0, 0);
      const { id } = await createProject({
        address: { street: street.trim(), city: city.trim(), postalCode: '', coordinates: null },
        contact: { name: name.trim(), phone: phone.trim() },
        dates: { evaluation: d ? Timestamp.fromDate(d) : null, scheduledPickup: null, actualPickup: null },
        status: 'bostad', generalNotes: '', evaluator: '',
      } as any);
      onCreated(id);
    } catch (e) {
      console.error('Quick add failed:', e);
      setErr('Kunde inte skapa projektet. Försök igen.');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); save(); }} className="p-4 space-y-3">
      <div>
        <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">Nytt projekt</h2>
        <p className="text-[13px] text-[var(--text-secondary)]">Adressen räcker nu. Resten fylls i senare.</p>
      </div>
      <input ref={first} value={street} onChange={e => { setStreet(e.target.value); if (err) setErr(null); }} placeholder="Gatuadress" autoComplete="street-address" aria-label="Gatuadress" className={controlClass} />
      <div className="grid grid-cols-2 gap-2">
        <input value={city} onChange={e => setCity(e.target.value)} placeholder="Ort" autoComplete="address-level2" aria-label="Ort" className={controlClass} />
        <DateField value={date} onChange={setDate} placeholder="Hembesök" aria-label="Datum för hembesök" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Kontaktperson" autoComplete="name" aria-label="Kontaktperson" className={controlClass} />
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Telefon" type="tel" autoComplete="tel" aria-label="Telefon" className={controlClass} />
      </div>
      {err && <p role="alert" className="text-[13px] text-red-700 dark:text-red-300">{err}</p>}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => { onClose(); navigate('/projects/new', { state: { draft: { street, city, name, phone, date: date ? date.toISOString() : null } } }); }}
          className="h-[32px] -ml-2 px-2 rounded-ios-control text-[13px] font-medium text-accent-700 dark:text-accent-300 hover:bg-[var(--bg-surface-alt)]"
        >
          Fyll i alla uppgifter
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Avbryt</Button>
          <Button size="sm" variant="primary" type="submit" loading={saving}>Skapa projekt</Button>
        </div>
      </div>
    </form>
  );
};

export default DesktopHome;
